import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Store, Plus, ImagePlus, X, Eye, Package, Settings, Trash2, ExternalLink, ShoppingBag, Tag, Pencil } from "lucide-react";
import { getMyShop, createShop, updateShop, deleteShop, uploadImage, resolveImageUrl, getShopListings, deleteListing, updateListing, createListingV2 } from "@/lib/api";
import { toast } from "sonner";
import DOMPurify from "dompurify";
import RichTextEditor, { isContentEmpty } from "@/components/RichTextEditor";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { ShareButton } from "@/components/ShareButton";

const CATEGORIES = ["Rosaries", "Bibles & Books", "Icons & Art", "Candles & Devotionals", "Handmade Crafts", "Clothing & Vestments", "Home & Garden", "Other"];

function ShopForm({ shop, onSave, onCancel, isCreating }) {
  const [name, setName] = useState(shop?.name || "");
  const [tagline, setTagline] = useState(shop?.tagline || "");
  const [description, setDescription] = useState(shop?.description || "");
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(shop?.logo_url ? resolveImageUrl(shop.logo_url) : null);
  const [bannerFile, setBannerFile] = useState(null);
  const [bannerPreview, setBannerPreview] = useState(shop?.banner_url ? resolveImageUrl(shop.banner_url) : null);
  // Location fields
  const [city, setCity] = useState(shop?.city || "");
  const [state, setState] = useState(shop?.state || "");
  const [zipCode, setZipCode] = useState(shop?.zip_code || "");
  const [country, setCountry] = useState(shop?.country || "us");
  const [saving, setSaving] = useState(false);
  
  const logoInputRef = useRef(null);
  const bannerInputRef = useRef(null);

  const handleImageSelect = (e, type) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be under 5MB");
      return;
    }
    
    const preview = URL.createObjectURL(file);
    if (type === "logo") {
      setLogoFile(file);
      setLogoPreview(preview);
    } else {
      setBannerFile(file);
      setBannerPreview(preview);
    }
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      toast.error("Shop name is required");
      return;
    }
    
    setSaving(true);
    
    try {
      let logoUrl = shop?.logo_url;
      let bannerUrl = shop?.banner_url;
      
      // Upload logo if changed
      if (logoFile) {
        const uploadRes = await uploadImage(logoFile);
        logoUrl = uploadRes.data.url;
      }
      
      // Upload banner if changed
      if (bannerFile) {
        const uploadRes = await uploadImage(bannerFile);
        bannerUrl = uploadRes.data.url;
      }
      
      await onSave({
        name: name.trim(),
        tagline: tagline.trim() || null,
        description: description || null,
        logo_url: logoUrl,
        banner_url: bannerUrl,
        city: city.trim() || null,
        state: state.trim() || null,
        zip_code: zipCode.trim() || null,
        country: country || "us"
      });
      
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save shop");
    }
    
    setSaving(false);
  };

  return (
    <div className="space-y-6">
      {/* Banner Upload */}
      <div className="space-y-2">
        <Label className="font-['Nunito'] text-sm text-[#2D2A26]">Shop Banner</Label>
        <input type="file" ref={bannerInputRef} accept="image/*" onChange={(e) => handleImageSelect(e, "banner")} className="hidden" />
        {bannerPreview ? (
          <div className="relative">
            <img src={bannerPreview} alt="Banner preview" className="w-full h-32 object-cover rounded-lg border border-[#E5E0D5]" />
            <button 
              onClick={() => { setBannerFile(null); setBannerPreview(null); }}
              className="absolute top-2 right-2 w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-md hover:bg-gray-100"
              data-testid="remove-banner-btn"
            >
              <X className="w-4 h-4 text-[#6B665E]" />
            </button>
          </div>
        ) : (
          <Button 
            variant="outline" 
            onClick={() => bannerInputRef.current?.click()}
            className="w-full h-32 border-dashed border-[#E5E0D5] text-[#6B665E] font-['Nunito'] flex flex-col gap-2"
            data-testid="upload-banner-btn"
          >
            <ImagePlus className="w-6 h-6" />
            Upload Banner (Recommended: 1200x300)
          </Button>
        )}
      </div>

      {/* Logo Upload */}
      <div className="space-y-2">
        <Label className="font-['Nunito'] text-sm text-[#2D2A26]">Shop Logo</Label>
        <input type="file" ref={logoInputRef} accept="image/*" onChange={(e) => handleImageSelect(e, "logo")} className="hidden" />
        <div className="flex items-center gap-4">
          {logoPreview ? (
            <div className="relative">
              <img src={logoPreview} alt="Logo preview" className="w-24 h-24 object-cover rounded-xl border border-[#E5E0D5]" />
              <button 
                onClick={() => { setLogoFile(null); setLogoPreview(null); }}
                className="absolute -top-2 -right-2 w-6 h-6 bg-white rounded-full flex items-center justify-center shadow-md hover:bg-gray-100"
                data-testid="remove-logo-btn"
              >
                <X className="w-3 h-3 text-[#6B665E]" />
              </button>
            </div>
          ) : (
            <Button 
              variant="outline" 
              onClick={() => logoInputRef.current?.click()}
              className="w-24 h-24 border-dashed border-[#E5E0D5] text-[#6B665E] font-['Nunito'] flex flex-col gap-1"
              data-testid="upload-logo-btn"
            >
              <ImagePlus className="w-5 h-5" />
              <span className="text-xs">Logo</span>
            </Button>
          )}
          <p className="text-xs text-[#6B665E] font-['Nunito']">Recommended: Square image, 200x200 or larger</p>
        </div>
      </div>

      {/* Shop Name */}
      <div className="space-y-2">
        <Label className="font-['Nunito'] text-sm text-[#2D2A26]">Shop Name *</Label>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g., Sacred Crafts by Maria"
          className="border-[#E5E0D5] font-['Nunito'] focus-visible:ring-[var(--liturgical-primary)]"
          data-testid="shop-name-input"
        />
      </div>

      {/* Tagline */}
      <div className="space-y-2">
        <Label className="font-['Nunito'] text-sm text-[#2D2A26]">Tagline</Label>
        <Input
          value={tagline}
          onChange={(e) => setTagline(e.target.value)}
          placeholder="A short catchy phrase for your shop"
          className="border-[#E5E0D5] font-['Nunito'] focus-visible:ring-[var(--liturgical-primary)]"
          data-testid="shop-tagline-input"
        />
      </div>

      {/* Description */}
      <div className="space-y-2">
        <Label className="font-['Nunito'] text-sm text-[#2D2A26]">Description</Label>
        <RichTextEditor
          value={description}
          onChange={setDescription}
          placeholder="Tell buyers about your shop, what you sell, your story..."
          minHeight="120px"
        />
      </div>

      {/* Location Section */}
      <div className="space-y-3">
        <Label className="font-['Nunito'] text-sm text-[#2D2A26] font-semibold">Shop Location (for distance-based search)</Label>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="font-['Nunito'] text-xs text-[#6B665E]">City</Label>
            <Input
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="e.g., Memphis"
              className="border-[#E5E0D5] font-['Nunito'] text-sm"
              data-testid="shop-city-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="font-['Nunito'] text-xs text-[#6B665E]">State</Label>
            <Input
              value={state}
              onChange={(e) => setState(e.target.value)}
              placeholder="e.g., TN"
              className="border-[#E5E0D5] font-['Nunito'] text-sm"
              data-testid="shop-state-input"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="font-['Nunito'] text-xs text-[#6B665E]">ZIP Code *</Label>
            <Input
              value={zipCode}
              onChange={(e) => setZipCode(e.target.value)}
              placeholder="e.g., 38107"
              className="border-[#E5E0D5] font-['Nunito'] text-sm"
              data-testid="shop-zip-input"
            />
            <p className="text-xs text-[#6B665E]">Required for buyers to filter by distance</p>
          </div>
          <div className="space-y-1.5">
            <Label className="font-['Nunito'] text-xs text-[#6B665E]">Country</Label>
            <select
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              className="w-full h-9 px-3 rounded-md border border-[#E5E0D5] font-['Nunito'] text-sm bg-white"
              data-testid="shop-country-select"
            >
              <option value="us">United States</option>
              <option value="ca">Canada</option>
              <option value="mx">Mexico</option>
              <option value="gb">United Kingdom</option>
            </select>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3 pt-4">
        {onCancel && (
          <Button
            variant="outline"
            onClick={onCancel}
            className="flex-1 border-[#E5E0D5] text-[#6B665E] font-['Nunito']"
            data-testid="shop-cancel-btn"
          >
            Cancel
          </Button>
        )}
        <Button
          onClick={handleSubmit}
          disabled={saving}
          className="flex-1 bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito'] font-semibold"
          data-testid="shop-save-btn"
        >
          {saving ? "Saving..." : isCreating ? "Create Shop" : "Save Changes"}
        </Button>
      </div>
    </div>
  );
}

function ShopListingCard({ listing, navigate, onEdit, onDelete }) {
  const isSold = listing.status === "sold";
  
  return (
    <Card className={`bg-white border border-[#E5E0D5] ${isSold ? 'opacity-60' : ''}`}>
      <CardContent className="p-3 flex gap-3">
        {listing.image_url ? (
          <img 
            src={resolveImageUrl(listing.image_url)} 
            alt={listing.title} 
            className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
          />
        ) : (
          <div className="w-16 h-16 rounded-lg bg-[#F0EBE0] flex items-center justify-center flex-shrink-0">
            <ShoppingBag className="w-6 h-6 text-[#E5E0D5]" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <h3 className="font-['Nunito'] font-semibold text-sm text-[#2D2A26] line-clamp-1">{listing.title}</h3>
            <span className="font-['Nunito'] font-bold text-[var(--liturgical-primary)] text-sm">${listing.price}</span>
          </div>
          <div className="flex items-center gap-2 mt-1">
            <Tag className="w-3 h-3 text-[#6B665E]" />
            <span className="text-xs text-[#6B665E] font-['Nunito']">{listing.category}</span>
            {isSold && (
              <span className="text-xs bg-[var(--liturgical-primary)] text-white px-2 py-0.5 rounded-full font-['Nunito']">Sold</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/listing/${listing.listing_id}`)}
            className="text-[#6B665E] hover:text-[var(--liturgical-primary)]"
            title="View"
          >
            <Eye className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onEdit(listing)}
            className="text-[#6B665E] hover:text-[var(--liturgical-primary)]"
            title="Edit"
          >
            <Pencil className="w-4 h-4" />
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="text-[#6B665E] hover:text-red-500"
                title="Delete"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="bg-white">
              <AlertDialogHeader>
                <AlertDialogTitle className="font-['Crimson_Text']">Delete Item?</AlertDialogTitle>
                <AlertDialogDescription className="font-['Nunito']">
                  Are you sure you want to delete "{listing.title}"? This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="font-['Nunito']">Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => onDelete(listing.listing_id)}
                  className="bg-red-600 hover:bg-red-700 font-['Nunito']"
                >
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </CardContent>
    </Card>
  );
}

export default function MyShopPage({ user }) {
  const navigate = useNavigate();
  const [shop, setShop] = useState(null);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [isEditing, setIsEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // Create/Edit item dialog state
  const [itemDialog, setItemDialog] = useState({ open: false, item: null });
  const [itemForm, setItemForm] = useState({ title: "", description: "", price: "", category: "", condition: "new" });
  const [itemImages, setItemImages] = useState([]);
  const [itemImagePreviews, setItemImagePreviews] = useState([]);
  const [savingItem, setSavingItem] = useState(false);
  const itemFileInputRef = useRef(null);

  useEffect(() => {
    loadShopData();
  }, []);

  const loadShopData = async () => {
    setLoading(true);
    try {
      const res = await getMyShop();
      setShop(res.data.shop);
      
      if (res.data.shop) {
        const listingsRes = await getShopListings(res.data.shop.shop_id, { limit: 50 });
        setListings(listingsRes.data.listings);
      }
    } catch (err) {
      toast.error("Failed to load shop data");
    }
    setLoading(false);
  };

  const handleCreateShop = async (data) => {
    try {
      const res = await createShop(data);
      setShop(res.data);
      toast.success("Shop created successfully!");
    } catch (err) {
      throw err;
    }
  };

  const handleUpdateShop = async (data) => {
    try {
      const res = await updateShop(shop.shop_id, data);
      setShop(res.data);
      setIsEditing(false);
      toast.success("Shop updated successfully!");
    } catch (err) {
      throw err;
    }
  };

  const handleDeleteShop = async () => {
    setDeleting(true);
    try {
      await deleteShop(shop.shop_id);
      setShop(null);
      setListings([]);
      toast.success("Shop deleted successfully");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete shop");
    }
    setDeleting(false);
  };

  // Item CRUD handlers
  const openCreateItemDialog = () => {
    setItemForm({ title: "", description: "", price: "", category: "", condition: "new" });
    setItemImages([]);
    setItemImagePreviews([]);
    setItemDialog({ open: true, item: null });
  };

  const openEditItemDialog = (item) => {
    setItemForm({
      title: item.title || "",
      description: item.description || "",
      price: item.price?.toString() || "",
      category: item.category || "",
      condition: item.condition || "new"
    });
    setItemImages([]);
    setItemImagePreviews(item.image_urls?.map(url => ({ url: resolveImageUrl(url), existing: true })) || 
                         (item.image_url ? [{ url: resolveImageUrl(item.image_url), existing: true }] : []));
    setItemDialog({ open: true, item });
  };

  const handleItemImageSelect = (e) => {
    const files = Array.from(e.target.files || []);
    const currentCount = itemImagePreviews.length;
    if (currentCount + files.length > 10) {
      toast.error("Maximum 10 images allowed");
      return;
    }
    
    const newPreviews = files.map(f => ({ url: URL.createObjectURL(f), existing: false }));
    setItemImages([...itemImages, ...files]);
    setItemImagePreviews([...itemImagePreviews, ...newPreviews]);
  };

  const removeItemImage = (index) => {
    const preview = itemImagePreviews[index];
    if (!preview.existing) {
      URL.revokeObjectURL(preview.url);
      const newFiles = [...itemImages];
      const newFileIndex = itemImagePreviews.slice(0, index).filter(p => !p.existing).length;
      newFiles.splice(newFileIndex, 1);
      setItemImages(newFiles);
    }
    const newPreviews = [...itemImagePreviews];
    newPreviews.splice(index, 1);
    setItemImagePreviews(newPreviews);
  };

  const handleSaveItem = async () => {
    if (!itemForm.title || !itemForm.price || !itemForm.category) {
      toast.error("Fill in all required fields (title, price, category)");
      return;
    }
    
    setSavingItem(true);
    try {
      // Upload new images
      const uploadedUrls = [];
      for (const file of itemImages) {
        const uploadRes = await uploadImage(file);
        uploadedUrls.push(uploadRes.data.url);
      }
      
      // Get existing image URLs
      const existingUrls = itemImagePreviews.filter(p => p.existing).map(p => {
        // Extract relative path from full URL
        const url = p.url;
        if (url.includes('/api/uploads/')) {
          return url.split('/api/uploads/')[1] ? `/api/uploads/${url.split('/api/uploads/')[1]}` : url;
        }
        return url;
      });
      
      const allImageUrls = [...existingUrls, ...uploadedUrls];
      
      if (itemDialog.item) {
        // Update existing item
        await updateListing(itemDialog.item.listing_id, {
          title: itemForm.title,
          description: itemForm.description,
          price: parseFloat(itemForm.price),
          category: itemForm.category,
          condition: itemForm.condition,
          image_urls: allImageUrls
        });
        toast.success("Item updated!");
      } else {
        // Create new item
        await createListingV2({
          title: itemForm.title,
          description: itemForm.description,
          price: parseFloat(itemForm.price),
          category: itemForm.category,
          condition: itemForm.condition,
          image_urls: allImageUrls,
          listing_type: "item",
          shop_id: shop.shop_id
        });
        toast.success("Item listed for sale!");
      }
      
      setItemDialog({ open: false, item: null });
      loadShopData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save item");
    }
    setSavingItem(false);
  };

  const handleDeleteItem = async (listingId) => {
    try {
      await deleteListing(listingId);
      toast.success("Item deleted");
      loadShopData();
    } catch (err) {
      toast.error("Failed to delete item");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F7F2] flex items-center justify-center">
        <div className="text-[#6B665E] font-['Nunito']">Loading your shop...</div>
      </div>
    );
  }

  // No shop yet - show create form
  if (!shop) {
    return (
      <div className="min-h-screen bg-[#F9F7F2]" data-testid="my-shop-page">
        <div className="max-w-2xl mx-auto px-4 py-8">
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-[var(--liturgical-primary)]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Store className="w-10 h-10 text-[var(--liturgical-primary)]" />
            </div>
            <h1 className="font-['Crimson_Text'] text-3xl font-bold text-[#2D2A26] mb-2">Create Your Shop</h1>
            <p className="text-[#6B665E] font-['Nunito']">
              Set up your storefront to showcase all your listings in one place
            </p>
          </div>
          
          <Card className="bg-white border-[#E5E0D5]">
            <CardContent className="p-6">
              <ShopForm 
                shop={null} 
                onSave={handleCreateShop}
                isCreating={true}
              />
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Has shop - show management interface
  return (
    <div className="min-h-screen bg-[#F9F7F2]" data-testid="my-shop-page">
      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-['Crimson_Text'] text-3xl font-bold text-[#2D2A26]">My Shop</h1>
            <p className="text-[#6B665E] font-['Nunito'] text-sm mt-1">Manage your storefront and items for sale</p>
          </div>
          <div className="flex gap-2">
            <ShareButton
              title={shop.name}
              description={shop.description || "Check out this shop on Parish"}
              url={`/shop/${shop.shop_id}`}
              image={shop.logo_url}
              type="shop"
              variant="outline"
              className="border-[#E5E0D5] text-[#6B665E] font-['Nunito']"
            />
            <Button
              variant="outline"
              onClick={() => navigate(`/shop/${shop.shop_id}`)}
              className="border-[#E5E0D5] text-[#2D2A26] font-['Nunito']"
              data-testid="view-shop-btn"
            >
              <Eye className="w-4 h-4 mr-2" /> View Shop
            </Button>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-[#F0EBE0] border border-[#E5E0D5] mb-6">
            <TabsTrigger value="overview" className="font-['Nunito'] data-[state=active]:bg-white" data-testid="tab-overview">
              <Store className="w-4 h-4 mr-2" /> Overview
            </TabsTrigger>
            <TabsTrigger value="listings" className="font-['Nunito'] data-[state=active]:bg-white" data-testid="tab-listings">
              <Package className="w-4 h-4 mr-2" /> Items for Sale ({listings.length})
            </TabsTrigger>
            <TabsTrigger value="settings" className="font-['Nunito'] data-[state=active]:bg-white" data-testid="tab-settings">
              <Settings className="w-4 h-4 mr-2" /> Settings
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid md:grid-cols-3 gap-6">
              {/* Shop Preview Card */}
              <Card className="md:col-span-2 bg-white border-[#E5E0D5]">
                <CardHeader className="pb-4">
                  <CardTitle className="font-['Crimson_Text'] text-xl flex items-center justify-between">
                    Shop Preview
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => setIsEditing(true)}
                      className="text-[var(--liturgical-primary)]"
                      data-testid="edit-shop-btn"
                    >
                      Edit
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {/* Banner */}
                  <div className="relative h-24 rounded-lg overflow-hidden mb-4 bg-gradient-to-r from-[var(--liturgical-primary)] to-[var(--liturgical-secondary)]">
                    {shop.banner_url && (
                      <img src={resolveImageUrl(shop.banner_url)} alt="Banner" className="w-full h-full object-cover" />
                    )}
                  </div>
                  
                  <div className="flex gap-4">
                    {/* Logo */}
                    {shop.logo_url ? (
                      <img src={resolveImageUrl(shop.logo_url)} alt={shop.name} className="w-16 h-16 rounded-xl object-cover border border-[#E5E0D5]" />
                    ) : (
                      <div className="w-16 h-16 rounded-xl bg-[#F0EBE0] flex items-center justify-center border border-[#E5E0D5]">
                        <Store className="w-8 h-8 text-[var(--liturgical-primary)]" />
                      </div>
                    )}
                    
                    {/* Info */}
                    <div className="flex-1">
                      <h3 className="font-['Crimson_Text'] text-xl font-bold text-[#2D2A26]">{shop.name}</h3>
                      {shop.tagline && <p className="text-sm text-[#6B665E] font-['Nunito']">{shop.tagline}</p>}
                    </div>
                  </div>
                  
                  {shop.description && (
                    <div 
                      className="mt-4 prose prose-sm max-w-none text-[#6B665E] font-['Nunito']"
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(shop.description) }}
                    />
                  )}
                </CardContent>
              </Card>

              {/* Stats Card */}
              <Card className="bg-white border-[#E5E0D5]">
                <CardHeader className="pb-4">
                  <CardTitle className="font-['Crimson_Text'] text-xl">Quick Stats</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="p-4 bg-[#F9F7F2] rounded-lg">
                    <p className="text-xs text-[#6B665E] font-['Nunito'] uppercase">Total Items</p>
                    <p className="text-2xl font-bold text-[#2D2A26] font-['Crimson_Text']">{shop.listing_count || 0}</p>
                  </div>
                  <div className="p-4 bg-[#F9F7F2] rounded-lg">
                    <p className="text-xs text-[#6B665E] font-['Nunito'] uppercase">Active Items</p>
                    <p className="text-2xl font-bold text-[var(--liturgical-primary)] font-['Crimson_Text']">
                      {listings.filter(l => l.status !== "sold").length}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    className="w-full border-[#E5E0D5] text-[#2D2A26] font-['Nunito']"
                    onClick={() => navigate(`/shop/${shop.shop_id}`)}
                  >
                    <ExternalLink className="w-4 h-4 mr-2" /> View Public Page
                  </Button>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Items for Sale Tab */}
          <TabsContent value="listings">
            <Card className="bg-white border-[#E5E0D5]">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="font-['Crimson_Text'] text-xl">Items for Sale</CardTitle>
                  <Button
                    size="sm"
                    onClick={openCreateItemDialog}
                    className="bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito']"
                    data-testid="add-item-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" /> Add New
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {listings.length === 0 ? (
                  <div className="text-center py-12">
                    <ShoppingBag className="w-12 h-12 text-[#E5E0D5] mx-auto mb-3" />
                    <p className="text-[#6B665E] font-['Nunito'] mb-4">No items for sale yet</p>
                    <Button
                      onClick={openCreateItemDialog}
                      className="bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito']"
                    >
                      Add Your First Item
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {listings.map(listing => (
                      <ShopListingCard 
                        key={listing.listing_id} 
                        listing={listing} 
                        navigate={navigate}
                        onEdit={openEditItemDialog}
                        onDelete={handleDeleteItem}
                      />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Edit Shop Card */}
              <Card className="bg-white border-[#E5E0D5]">
                <CardHeader>
                  <CardTitle className="font-['Crimson_Text'] text-xl">Edit Shop Details</CardTitle>
                </CardHeader>
                <CardContent>
                  <ShopForm 
                    shop={shop} 
                    onSave={handleUpdateShop}
                    isCreating={false}
                  />
                </CardContent>
              </Card>

              {/* Danger Zone */}
              <Card className="bg-white border-red-200">
                <CardHeader>
                  <CardTitle className="font-['Crimson_Text'] text-xl text-red-600">Danger Zone</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-[#6B665E] font-['Nunito'] mb-4">
                    Deleting your shop will unlink all listings from it. The listings will remain but won't be grouped under your shop anymore.
                  </p>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="destructive"
                        className="w-full font-['Nunito']"
                        data-testid="delete-shop-btn"
                      >
                        <Trash2 className="w-4 h-4 mr-2" /> Delete Shop
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="bg-white">
                      <AlertDialogHeader>
                        <AlertDialogTitle className="font-['Crimson_Text']">Delete Shop?</AlertDialogTitle>
                        <AlertDialogDescription className="font-['Nunito']">
                          This action cannot be undone. Your shop will be permanently deleted and all listings will be unlinked from it.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="font-['Nunito']">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={handleDeleteShop}
                          disabled={deleting}
                          className="bg-red-600 hover:bg-red-700 font-['Nunito']"
                        >
                          {deleting ? "Deleting..." : "Delete Shop"}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>

        {/* Edit Modal */}
        {isEditing && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <CardHeader>
                <CardTitle className="font-['Crimson_Text'] text-xl">Edit Shop</CardTitle>
              </CardHeader>
              <CardContent>
                <ShopForm 
                  shop={shop} 
                  onSave={handleUpdateShop}
                  onCancel={() => setIsEditing(false)}
                  isCreating={false}
                />
              </CardContent>
            </Card>
          </div>
        )}

        {/* Create/Edit Item Dialog */}
        <Dialog open={itemDialog.open} onOpenChange={(open) => setItemDialog({ ...itemDialog, open })}>
          <DialogContent className="bg-white border-[#E5E0D5] max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-['Crimson_Text'] text-xl text-[#2D2A26]">
                {itemDialog.item ? "Edit Item" : "Add Item for Sale"}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 mt-4">
              {/* Title */}
              <div className="space-y-1.5">
                <Label className="font-['Nunito'] text-xs text-[#2D2A26]">Title *</Label>
                <Input
                  value={itemForm.title}
                  onChange={(e) => setItemForm({ ...itemForm, title: e.target.value })}
                  placeholder="What are you selling?"
                  className="border-[#E5E0D5] font-['Nunito'] text-sm"
                />
              </div>

              {/* Category & Condition */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="font-['Nunito'] text-xs text-[#2D2A26]">Category *</Label>
                  <Select value={itemForm.category} onValueChange={(v) => setItemForm({ ...itemForm, category: v })}>
                    <SelectTrigger className="border-[#E5E0D5] text-sm font-['Nunito']">
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-[#E5E0D5]">
                      {CATEGORIES.map(c => (
                        <SelectItem key={c} value={c} className="font-['Nunito'] text-sm">{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="font-['Nunito'] text-xs text-[#2D2A26]">Condition</Label>
                  <Select value={itemForm.condition} onValueChange={(v) => setItemForm({ ...itemForm, condition: v })}>
                    <SelectTrigger className="border-[#E5E0D5] text-sm font-['Nunito']">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-[#E5E0D5]">
                      <SelectItem value="new" className="font-['Nunito'] text-sm">New</SelectItem>
                      <SelectItem value="like_new" className="font-['Nunito'] text-sm">Like New</SelectItem>
                      <SelectItem value="good" className="font-['Nunito'] text-sm">Good</SelectItem>
                      <SelectItem value="fair" className="font-['Nunito'] text-sm">Fair</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Price */}
              <div className="space-y-1.5">
                <Label className="font-['Nunito'] text-xs text-[#2D2A26]">Price ($) *</Label>
                <Input
                  type="number"
                  value={itemForm.price}
                  onChange={(e) => setItemForm({ ...itemForm, price: e.target.value })}
                  placeholder="0.00"
                  className="border-[#E5E0D5] font-['Nunito'] text-sm"
                  min="0"
                  step="0.01"
                />
              </div>

              {/* Description */}
              <div className="space-y-1.5">
                <Label className="font-['Nunito'] text-xs text-[#2D2A26]">Description</Label>
                <RichTextEditor
                  value={itemForm.description}
                  onChange={(v) => setItemForm({ ...itemForm, description: v })}
                  placeholder="Describe your item..."
                  minHeight="100px"
                  minimal={true}
                />
              </div>

              {/* Images */}
              <div className="space-y-1.5">
                <Label className="font-['Nunito'] text-xs text-[#2D2A26]">Photos (up to 10)</Label>
                <input
                  type="file"
                  ref={itemFileInputRef}
                  accept="image/*"
                  multiple
                  onChange={handleItemImageSelect}
                  className="hidden"
                />
                
                {itemImagePreviews.length > 0 ? (
                  <div className="space-y-2">
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                      {itemImagePreviews.map((preview, idx) => (
                        <div key={idx} className="relative aspect-square">
                          <img src={preview.url} alt={`Preview ${idx + 1}`} className="w-full h-full object-cover rounded-md border border-[#E5E0D5]" />
                          <button 
                            onClick={() => removeItemImage(idx)} 
                            className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                      {itemImagePreviews.length < 10 && (
                        <button 
                          onClick={() => itemFileInputRef.current?.click()} 
                          className="aspect-square border-2 border-dashed border-[#E5E0D5] rounded-md flex flex-col items-center justify-center text-[#6B665E] hover:border-[var(--liturgical-primary)] hover:text-[var(--liturgical-primary)] transition-colors"
                        >
                          <ImagePlus className="w-5 h-5" />
                          <span className="text-xs mt-1">Add</span>
                        </button>
                      )}
                    </div>
                    <p className="text-xs text-[#6B665E]">{itemImagePreviews.length}/10 photos</p>
                  </div>
                ) : (
                  <Button 
                    variant="outline" 
                    onClick={() => itemFileInputRef.current?.click()} 
                    className="w-full border-dashed border-[#E5E0D5] text-[#6B665E] font-['Nunito'] text-xs h-20 flex flex-col gap-1"
                  >
                    <ImagePlus className="w-5 h-5" />
                    Upload Photos
                  </Button>
                )}
              </div>

              {/* Submit Button */}
              <Button
                onClick={handleSaveItem}
                disabled={savingItem}
                className="w-full bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito'] font-semibold h-10"
              >
                {savingItem ? "Saving..." : itemDialog.item ? "Save Changes" : "List Item for Sale"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
